import React, { useMemo } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';
import { CheckboxField, RadioGroupField } from '@brighte/form-components';
import { EligibilityType, FINANCIAL_PRODUCT, isBusinessLoanProduct, nameof } from '@brighte/validation';
import CustomToolTip from '@components/CustomToolTip/CustomToolTip';
import { useApplicationData } from '@provider';
import { Box } from '@spark-web/box';
import { Columns } from '@spark-web/columns';
import { Heading } from '@spark-web/heading';
import { RadioCard } from '@spark-web/radio';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextList } from '@spark-web/text-list';
import { ELIGIBILITY } from '../../utils';
import { getFinanceTermsByFinancialProduct, getPrivacyTermsByFinancialProduct } from './helpers';

type TermsSectionProps = Pick<UseFormReturn<EligibilityType>, 'control'> & {
  filteredTerms: string[];
  heading: string;
  name: FieldPath<EligibilityType>;
  terms: typeof ELIGIBILITY.FINANCE_TERMS | typeof ELIGIBILITY.PRIVACY_TERMS;
};

type TermsProps = Pick<UseFormReturn<EligibilityType>, 'control'> & {
  financialProductId: string;
};

type CustomerDeclarationProps = TermsProps;

const TermsSection = ({ name, heading, filteredTerms, terms, control }: TermsSectionProps) => {
  const renderListItem = (fieldValue: string | React.ReactNode) => {
    if (typeof fieldValue === 'string') {
      return <Text>{fieldValue}</Text>;
    }

    return fieldValue;
  };

  return (
    <Stack gap="xxlarge">
      <Heading level="4" tone="neutral">
        {heading}
      </Heading>
      <TextList>
        {filteredTerms.map((field: string) => (
          <React.Fragment key={field}>{terms[field] && renderListItem(terms[field])}</React.Fragment>
        ))}
      </TextList>
      <Box border="neutral" padding="large" borderRadius="small">
        <CheckboxField name={name} label={ELIGIBILITY.AGREE_TO_ALL} control={control} />
      </Box>
    </Stack>
  );
};

function NswProgramQuestions({ control }: Pick<UseFormReturn<EligibilityType>, 'control'>) {
  const { NSW_PROGRAM_QUESTIONS } = ELIGIBILITY;
  const { HEADING, SUBHEADING, YES, NO, QUESTIONS, TOOLTIP } = NSW_PROGRAM_QUESTIONS;

  const questionFields: { name: FieldPath<EligibilityType>; label: string; tooltipText: string }[] = [
    { name: 'sresClaimed', label: QUESTIONS.sresClaimed, tooltipText: TOOLTIP.sresClaimed },
    { name: 'nswSafeguardClaimed', label: QUESTIONS.nswSafeguardClaimed, tooltipText: TOOLTIP.nswSafeguardClaimed },
    { name: 'nathersAssessment', label: QUESTIONS.nathersAssessment, tooltipText: TOOLTIP.nathersAssessment },
  ];

  return (
    <Stack gap="xxlarge">
      <Heading level="4" tone="neutral">
        {HEADING}
      </Heading>
      <Text>{SUBHEADING}</Text>
      {questionFields.map(({ name, label, tooltipText }) => (
        <RadioGroupField
          key={name}
          name={name}
          label={label}
          control={control}
          FieldProps={{
            adornment: <CustomToolTip id={`${name}-tooltip`} text={tooltipText} />,
          }}
        >
          <Columns gap="large">
            <RadioCard value={true}>
              <Text>{YES}</Text>
            </RadioCard>
            <RadioCard value={false}>
              <Text>{NO}</Text>
            </RadioCard>
          </Columns>
        </RadioGroupField>
      ))}
    </Stack>
  );
}

function Terms({ control, financialProductId }: TermsProps) {
  const {
    HEADING_FINANCE_ELIGIBILITY,
    HEADING_PRIVACY_AND_ELECTRONIC_COMMUNICATION,
    PRIVACY_TERMS,
    FINANCE_TERMS,
    HEADING_FINANCE_ELIGIBILITY_BNPL,
  } = ELIGIBILITY;

  const isCashApplication = useApplicationData('Finance')?.payment?.isCashApplication;
  const isBNPLApplication = financialProductId === FINANCIAL_PRODUCT.BRIGHTE_PAY;
  const isBusinessProduct = isBusinessLoanProduct(financialProductId);
  const isHesl = financialProductId === FINANCIAL_PRODUCT.BHESL;

  const termsSections = useMemo(() => {
    type Section = {
      name: FieldPath<EligibilityType>;
      heading: string;
      filteredTerms: string[];
      terms: typeof FINANCE_TERMS | typeof PRIVACY_TERMS;
    };
    const bheslFinanceTerms = isHesl
      ? { ...FINANCE_TERMS, beOver18: FINANCE_TERMS.beOver18AustralianCitizen }
      : FINANCE_TERMS;

    const sections: Section[] = [
      {
        name: nameof<EligibilityType>('agreeToFinanceTerms'),
        heading: isBNPLApplication ? HEADING_FINANCE_ELIGIBILITY_BNPL : HEADING_FINANCE_ELIGIBILITY,
        filteredTerms: getFinanceTermsByFinancialProduct(financialProductId),
        terms: bheslFinanceTerms,
      },
    ];

    if (!isBusinessProduct) {
      sections.push({
        name: nameof<EligibilityType>('agreeToPrivacyTerms'),
        heading: HEADING_PRIVACY_AND_ELECTRONIC_COMMUNICATION,
        filteredTerms: getPrivacyTermsByFinancialProduct(financialProductId, isCashApplication),
        terms: PRIVACY_TERMS,
      });
    }

    return sections;
  }, [financialProductId, isBusinessProduct, isCashApplication]);

  return (
    <Stack gap="xxlarge">
      {termsSections.map(({ name, heading, filteredTerms, terms }) =>
        filteredTerms.length > 0 ? (
          <TermsSection
            key={name}
            name={name}
            heading={heading}
            filteredTerms={filteredTerms}
            terms={terms}
            control={control}
          />
        ) : null,
      )}
      {isHesl && <NswProgramQuestions control={control} />}
    </Stack>
  );
}

export function CustomerDeclaration({ control, financialProductId }: CustomerDeclarationProps) {
  const {
    AGREE_TO_ALL,
    CUSTOMER_DECLARATION_HEADING,
    CUSTOMER_DECLARATION_DESCRIPTION,
    DECLARATION_CREDIT_BUSINESS,
    CONSENT_MARKETING_INFO,
    SME_TERMS_AND_CONDITIONS,
    ACT_COMMUNITY_TERMS_AND_CONDITIONS,
    ACT_COMMUNITY_SHS_CONSENT,
  } = ELIGIBILITY;
  const isSme = isBusinessLoanProduct(financialProductId);
  const isActCommunity = financialProductId === FINANCIAL_PRODUCT.ACT_COMMUNITY;
  const termsAndConditions = isActCommunity ? ACT_COMMUNITY_TERMS_AND_CONDITIONS : SME_TERMS_AND_CONDITIONS;
  const declarations = isSme ? [DECLARATION_CREDIT_BUSINESS, CONSENT_MARKETING_INFO] : [];

  return (
    <Stack gap="xxlarge">
      <Heading level="4" tone="neutral">
        {CUSTOMER_DECLARATION_HEADING}
      </Heading>

      {isSme ? (
        <TextList>
          {declarations.map(declaration => (
            <Text key={declaration}>{declaration}</Text>
          ))}
          <React.Fragment key="termsAndConditions">{termsAndConditions}</React.Fragment>
          {isActCommunity && <React.Fragment key="actShsConsent">{ACT_COMMUNITY_SHS_CONSENT}</React.Fragment>}
        </TextList>
      ) : null}

      <Box border="neutral" padding="large" borderRadius="small">
        <CheckboxField
          name="agreeToCustomerDeclaration"
          label={isSme ? AGREE_TO_ALL : CUSTOMER_DECLARATION_DESCRIPTION}
          control={control}
        />
      </Box>
    </Stack>
  );
}

export default Terms;
